import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useOperator } from '../lib/operator-context';
import OperatorChat from '../components/OperatorChat';
import { toast } from 'sonner';
import {
  Shield,
  AlertTriangle,
  Headphones,
  Activity,
  MapPin,
  Phone,
  Mail,
  Clock,
  User,
  Car,
  Truck,
  MessageCircle,
  ExternalLink,
  RefreshCw,
  LogOut,
  Eye,
  CheckCircle2,
  ArrowUpRight,
  X,
  ChevronRight,
  Globe,
  Zap,
  TrendingUp,
  ShieldCheck,
  ShieldAlert,
  ShieldX
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';

// Custom marker icons
const createIcon = (color) => L.divIcon({
  className: 'custom-div-icon',
  html: `<div style="background-color: ${color}; width: 24px; height: 24px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const emergencyIcon = createIcon('#EF4444');
const userIcon = createIcon('#3B82F6');
const driverIcon = createIcon('#22C55E');

// Risk Score Badge Component
const RiskBadge = ({ score, level, verified }) => {
  const getBgColor = () => {
    if (level === 'green') return 'bg-green-100 text-green-700 border-green-200';
    if (level === 'yellow') return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    return 'bg-red-100 text-red-700 border-red-200';
  };
  
  const getIcon = () => {
    if (level === 'green') return <ShieldCheck className="w-3 h-3" />;
    if (level === 'yellow') return <ShieldAlert className="w-3 h-3" />;
    return <ShieldX className="w-3 h-3" />;
  };

  return (
    <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${getBgColor()}`}>
      {getIcon()}
      <span>{score}</span>
      {verified && <span className="text-[10px]">✓</span>}
    </div>
  );
};

// Severity Badge Component
const SeverityBadge = ({ severity }) => {
  const getColor = () => {
    switch (severity) {
      case 'critical': return 'bg-red-600 text-white';
      case 'high': return 'bg-orange-500 text-white';
      case 'medium': return 'bg-yellow-500 text-white';
      default: return 'bg-blue-500 text-white';
    }
  };
  
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${getColor()}`}>
      {severity}
    </span>
  );
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const getColor = () => {
    switch (status) {
      case 'active': return 'bg-red-100 text-red-600 border-red-200';
      case 'acknowledged': return 'bg-yellow-100 text-yellow-600 border-yellow-200';
      case 'in_progress': return 'bg-blue-100 text-blue-600 border-blue-200';
      case 'escalated': return 'bg-purple-100 text-purple-600 border-purple-200';
      case 'resolved': return 'bg-green-100 text-green-600 border-green-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };
  
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getColor()}`}>
      {status}
    </span>
  );
};

// Format elapsed time
const formatElapsed = (seconds) => {
  if (!seconds) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${Math.round(seconds / 3600)}h ${Math.round((seconds % 3600) / 60)}m`;
};

export default function OperatorConsole() {
  const navigate = useNavigate();
  const {
    operator,
    isAuthenticated,
    loading: authLoading,
    logout,
    dashboard,
    loadDashboard,
    emergencies,
    loadEmergencies,
    getEmergencyDetail,
    takeEmergency,
    acknowledgeEmergency,
    escalateEmergency,
    resolveEmergency,
    addEmergencyNote,
    activeServices,
    loadActiveServices,
    supportTickets,
    loadSupportTickets,
    getWhatsAppLink,
    featureFlags
  } = useOperator();

  const [activeTab, setActiveTab] = useState('emergencies');
  const [selectedEmergency, setSelectedEmergency] = useState(null);
  const [emergencyDetail, setEmergencyDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [note, setNote] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [showResolutionModal, setShowResolutionModal] = useState(false);

  // Check auth
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/operator-login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Initial data load
  useEffect(() => {
    if (isAuthenticated) {
      loadDashboard();
      loadEmergencies();
      loadActiveServices();
      if (featureFlags.support_center_enabled) {
        loadSupportTickets();
      }
    }
  }, [isAuthenticated, loadDashboard, loadEmergencies, loadActiveServices, loadSupportTickets, featureFlags]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const interval = setInterval(() => {
      loadDashboard();
      loadEmergencies();
      loadActiveServices();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [isAuthenticated, loadDashboard, loadEmergencies, loadActiveServices]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        loadDashboard(),
        loadEmergencies(),
        loadActiveServices()
      ]);
      toast.success('Datos actualizados');
    } catch {
      toast.error('Error al actualizar');
    } finally {
      setRefreshing(false);
    }
  };

  const openEmergencyDetail = async (emergency) => {
    setSelectedEmergency(emergency);
    setDetailLoading(true);
    try {
      const detail = await getEmergencyDetail(emergency.id);
      setEmergencyDetail(detail);
    } catch {
      toast.error('Error al cargar detalles');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleTakeEmergency = async (alertId) => {
    try {
      await takeEmergency(alertId);
      toast.success('Emergencia asignada');
      if (selectedEmergency?.id === alertId) {
        const detail = await getEmergencyDetail(alertId);
        setEmergencyDetail(detail);
      }
    } catch {
      toast.error('Error al tomar emergencia');
    }
  };

  const handleAcknowledge = async (alertId) => {
    try {
      await acknowledgeEmergency(alertId);
      toast.success('Emergencia reconocida');
      if (selectedEmergency?.id === alertId) {
        const detail = await getEmergencyDetail(alertId);
        setEmergencyDetail(detail);
      }
    } catch {
      toast.error('Error al reconocer');
    }
  };

  const handleEscalate = async (alertId) => {
    if (!window.confirm('¿Escalar a autoridades externas? Esta acción es irreversible.')) return;
    try {
      await escalateEmergency(alertId);
      toast.success('Emergencia escalada');
      if (selectedEmergency?.id === alertId) {
        const detail = await getEmergencyDetail(alertId);
        setEmergencyDetail(detail);
      }
    } catch {
      toast.error('Error al escalar');
    }
  };

  const handleAddNote = async (alertId) => {
    if (!note.trim()) return;
    try {
      await addEmergencyNote(alertId, note);
      setNote('');
      toast.success('Nota agregada');
      const detail = await getEmergencyDetail(alertId);
      setEmergencyDetail(detail);
    } catch {
      toast.error('Error al agregar nota');
    }
  };

  const handleResolve = async () => {
    if (!resolutionNotes.trim()) {
      toast.error('Ingresa notas de resolución');
      return;
    }
    try {
      await resolveEmergency(selectedEmergency.id, resolutionNotes);
      toast.success('Emergencia resuelta');
      setShowResolutionModal(false);
      setResolutionNotes('');
      setSelectedEmergency(null);
      setEmergencyDetail(null);
    } catch {
      toast.error('Error al resolver');
    }
  };

  const handleWhatsApp = async (userId, type = 'user') => {
    try {
      const context = selectedEmergency ? 'emergency' : 'support';
      const result = await getWhatsAppLink(userId, context, selectedEmergency?.id);
      window.open(result.url, '_blank');
    } catch {
      toast.error('Error al generar enlace de WhatsApp');
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-red-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-slate-900 text-white py-3 px-4 sticky top-0 z-50">
        <div className="max-w-[1800px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-lg bg-red-500 flex items-center justify-center">
                <Shield className="w-6 h-6" />
              </div>
              <div>
                <h1 className="font-bold text-lg leading-tight">Central de Monitoreo</h1>
                <p className="text-slate-400 text-xs">MERCAPOLI MOVE</p>
              </div>
            </div>
            
            {/* Stats Quick View */}
            {dashboard && (
              <div className="hidden lg:flex items-center gap-4 ml-8 text-sm">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-red-500/20 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                  <span className="text-red-300 font-medium">{dashboard.emergencies?.active || 0}</span>
                  <span className="text-red-400/70">activas</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-700 rounded-lg">
                  <Activity className="w-4 h-4 text-blue-400" />
                  <span className="text-blue-300 font-medium">{dashboard.services?.total_active || 0}</span>
                  <span className="text-slate-400">servicios</span>
                </div>
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              className="text-slate-300 hover:text-white hover:bg-slate-700"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
            
            <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 rounded-lg">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-slate-300 text-sm">{operator?.full_name}</span>
              {operator?.country && (
                <span className="text-slate-500 text-xs">({operator.country})</span>
              )}
            </div>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              className="text-slate-400 hover:text-red-400 hover:bg-slate-700"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-[1800px] mx-auto p-4">
        {/* Main Content */}
        <div className="grid lg:grid-cols-3 gap-4">
          {/* Left Column - Emergency Queue */}
          <div className="lg:col-span-1 space-y-4">
            <Card className="bg-white border-slate-200">
              <CardHeader className="py-3 px-4 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-slate-800 text-sm flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                    Cola de Emergencias
                  </CardTitle>
                  <span className="px-2 py-0.5 bg-red-100 text-red-600 rounded text-xs font-medium">
                    {emergencies.length}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="p-2 max-h-[calc(100vh-280px)] overflow-y-auto">
                {emergencies.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <CheckCircle2 className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Sin emergencias activas</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {emergencies.map((em) => (
                      <div
                        key={em.id}
                        onClick={() => openEmergencyDetail(em)}
                        className={`p-3 rounded-lg cursor-pointer transition-all ${
                          selectedEmergency?.id === em.id 
                            ? 'bg-red-50 border-2 border-red-300' 
                            : em.severity === 'critical'
                              ? 'bg-red-50/50 border border-red-200 hover:bg-red-50'
                              : 'bg-slate-50 border border-slate-200 hover:bg-slate-100'
                        }`}
                        data-testid={`emergency-item-${em.id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <SeverityBadge severity={em.severity} />
                              <StatusBadge status={em.status} />
                              {em.risk_score && (
                                <RiskBadge 
                                  score={em.risk_score.score} 
                                  level={em.risk_score.level}
                                  verified={em.risk_score.verified_safe}
                                />
                              )}
                            </div>
                            <p className="text-slate-800 font-medium mt-2 truncate">
                              {em.user?.full_name || 'Usuario'}
                            </p>
                            <p className="text-slate-500 text-xs mt-1 line-clamp-2">
                              {em.initial_message || 'Sin mensaje'}
                            </p>
                            <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatElapsed(em.elapsed_seconds)}
                              </span>
                              <span className="flex items-center gap-1">
                                {em.service_type === 'ride' ? <Car className="w-3 h-3" /> : <Truck className="w-3 h-3" />}
                                {em.service_type}
                              </span>
                            </div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Active Services Quick View */}
            <Card className="bg-white border-slate-200">
              <CardHeader className="py-3 px-4 border-b border-slate-100">
                <CardTitle className="text-slate-800 text-sm flex items-center gap-2">
                  <Activity className="w-4 h-4 text-blue-500" />
                  Servicios Activos
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded text-xs font-medium ml-auto">
                    {activeServices.length}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-2 max-h-48 overflow-y-auto">
                {activeServices.length === 0 ? (
                  <p className="text-center py-4 text-slate-400 text-sm">Sin servicios activos</p>
                ) : (
                  <div className="space-y-1">
                    {activeServices.slice(0, 5).map((service) => (
                      <div key={service.id} className="p-2 bg-slate-50 rounded-lg flex items-center gap-2">
                        {service.service_type === 'ride' ? (
                          <Car className="w-4 h-4 text-green-500" />
                        ) : (
                          <Truck className="w-4 h-4 text-blue-500" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-slate-700 truncate">
                            {service.passenger?.full_name || service.customer?.full_name || 'Usuario'}
                          </p>
                          <p className="text-xs text-slate-400">{service.status}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Center Column - Detail Panel */}
          <div className="lg:col-span-2">
            {selectedEmergency && emergencyDetail ? (
              <div className="grid lg:grid-cols-2 gap-4">
                {/* Emergency Detail */}
                <Card className="bg-white border-slate-200">
                  <CardHeader className="py-3 px-4 border-b border-slate-100">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-slate-800 text-sm flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-500" />
                        Detalle de Emergencia
                      </CardTitle>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedEmergency(null);
                          setEmergencyDetail(null);
                        }}
                        className="text-slate-400 hover:text-slate-600 -mr-2"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="p-4 space-y-4 max-h-[calc(100vh-280px)] overflow-y-auto">
                    {detailLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
                      </div>
                    ) : (
                      <>
                        {/* Status & Severity */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <SeverityBadge severity={emergencyDetail.severity} />
                          <StatusBadge status={emergencyDetail.status} />
                          {emergencyDetail.user_risk && (
                            <RiskBadge 
                              score={emergencyDetail.user_risk.score}
                              level={emergencyDetail.user_risk.level}
                              verified={emergencyDetail.user_risk.verified_safe}
                            />
                          )}
                        </div>

                        {/* Message */}
                        <div className="p-3 bg-red-50 border border-red-100 rounded-lg">
                          <p className="text-sm text-red-800 font-medium">Mensaje de emergencia:</p>
                          <p className="text-red-700 mt-1">{emergencyDetail.initial_message || 'Sin mensaje'}</p>
                        </div>

                        {/* User Info */}
                        {emergencyDetail.user && (
                          <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-blue-500" />
                                <span className="font-medium text-blue-800">Usuario</span>
                              </div>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleWhatsApp(emergencyDetail.user.id, 'user')}
                                className="h-7 text-xs text-green-600 hover:bg-green-100"
                              >
                                <Phone className="w-3 h-3 mr-1" />
                                WhatsApp
                              </Button>
                            </div>
                            <div className="mt-2 space-y-1 text-sm">
                              <p className="text-blue-800">{emergencyDetail.user.full_name}</p>
                              <p className="text-blue-600 flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {emergencyDetail.user.phone || 'Sin teléfono'}
                              </p>
                              <p className="text-blue-600 flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {emergencyDetail.user.email}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Driver Info */}
                        {emergencyDetail.driver && (
                          <div className="p-3 bg-green-50 border border-green-100 rounded-lg">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Car className="w-4 h-4 text-green-500" />
                                <span className="font-medium text-green-800">Conductor</span>
                              </div>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleWhatsApp(emergencyDetail.driver.id, 'driver')}
                                className="h-7 text-xs text-green-600 hover:bg-green-100"
                              >
                                <Phone className="w-3 h-3 mr-1" />
                                WhatsApp
                              </Button>
                            </div>
                            <div className="mt-2 space-y-1 text-sm">
                              <p className="text-green-800">{emergencyDetail.driver.full_name}</p>
                              <p className="text-green-600 flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {emergencyDetail.driver.phone || 'Sin teléfono'}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Service Context */}
                        {emergencyDetail.service && (
                          <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                            <p className="font-medium text-slate-700 text-sm mb-2">Servicio Asociado</p>
                            <div className="text-xs text-slate-600 space-y-1">
                              <p>Tipo: {emergencyDetail.service_type}</p>
                              <p>Estado: {emergencyDetail.service.status}</p>
                              <p>ID: {emergencyDetail.service.id?.slice(0, 8)}...</p>
                            </div>
                          </div>
                        )}

                        {/* Location Map */}
                        {emergencyDetail.location && (
                          <div className="h-40 rounded-lg overflow-hidden border border-slate-200">
                            <MapContainer
                              center={[emergencyDetail.location.lat, emergencyDetail.location.lng]}
                              zoom={15}
                              style={{ height: '100%', width: '100%' }}
                              zoomControl={false}
                            >
                              <TileLayer
                                url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                                attribution='&copy; OpenStreetMap'
                              />
                              <Marker 
                                position={[emergencyDetail.location.lat, emergencyDetail.location.lng]}
                                icon={emergencyIcon}
                              >
                                <Popup>Ubicación de emergencia</Popup>
                              </Marker>
                            </MapContainer>
                          </div>
                        )}

                        {/* Timeline */}
                        {emergencyDetail.timeline && emergencyDetail.timeline.length > 0 && (
                          <div className="border-t border-slate-100 pt-4">
                            <p className="font-medium text-slate-700 text-sm mb-3">Timeline</p>
                            <div className="space-y-2 max-h-40 overflow-y-auto">
                              {emergencyDetail.timeline.map((event, idx) => (
                                <div key={idx} className="flex gap-2 text-xs">
                                  <div className="w-2 h-2 rounded-full bg-slate-400 mt-1.5 flex-shrink-0" />
                                  <div>
                                    <p className="text-slate-600">
                                      <span className="font-medium">{event.event_type || event.action}</span>
                                      {event.actor_name && ` por ${event.actor_name}`}
                                    </p>
                                    <p className="text-slate-400">
                                      {new Date(event.timestamp).toLocaleString('es-GT')}
                                    </p>
                                    {event.details && (
                                      <p className="text-slate-500 mt-0.5">
                                        {typeof event.details === 'string' 
                                          ? event.details 
                                          : event.details.message || event.details.action || JSON.stringify(event.details).slice(0, 100)}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Add Note */}
                        <div className="border-t border-slate-100 pt-4">
                          <p className="font-medium text-slate-700 text-sm mb-2">Agregar Nota</p>
                          <div className="flex gap-2">
                            <Input
                              value={note}
                              onChange={(e) => setNote(e.target.value)}
                              placeholder="Escribe una nota..."
                              className="flex-1 text-sm"
                            />
                            <Button
                              onClick={() => handleAddNote(emergencyDetail.id)}
                              disabled={!note.trim()}
                              className="bg-slate-700 hover:bg-slate-800"
                              size="sm"
                            >
                              Agregar
                            </Button>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="border-t border-slate-100 pt-4 space-y-2">
                          {emergencyDetail.status === 'active' && (
                            <div className="grid grid-cols-2 gap-2">
                              <Button
                                onClick={() => handleAcknowledge(emergencyDetail.id)}
                                className="bg-yellow-500 hover:bg-yellow-600 text-white"
                              >
                                <CheckCircle2 className="w-4 h-4 mr-2" />
                                Reconocer
                              </Button>
                              <Button
                                onClick={() => handleTakeEmergency(emergencyDetail.id)}
                                className="bg-blue-500 hover:bg-blue-600 text-white"
                              >
                                <User className="w-4 h-4 mr-2" />
                                Tomar Caso
                              </Button>
                            </div>
                          )}
                          
                          {['acknowledged', 'in_progress'].includes(emergencyDetail.status) && (
                            <div className="grid grid-cols-2 gap-2">
                              <Button
                                onClick={() => handleEscalate(emergencyDetail.id)}
                                variant="outline"
                                className="border-purple-300 text-purple-600 hover:bg-purple-50"
                              >
                                <ArrowUpRight className="w-4 h-4 mr-2" />
                                Escalar
                              </Button>
                              <Button
                                onClick={() => setShowResolutionModal(true)}
                                className="bg-green-500 hover:bg-green-600 text-white"
                              >
                                <CheckCircle2 className="w-4 h-4 mr-2" />
                                Resolver
                              </Button>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Chat Panel */}
                <div className="h-[calc(100vh-280px)]">
                  <OperatorChat
                    alertId={selectedEmergency.id}
                    userId={emergencyDetail?.user?.id}
                    driverId={emergencyDetail?.driver?.id}
                    userName={emergencyDetail?.user?.full_name}
                    driverName={emergencyDetail?.driver?.full_name}
                    onWhatsAppClick={handleWhatsApp}
                  />
                </div>
              </div>
            ) : (
              /* No Emergency Selected - Show Map Overview */
              <Card className="bg-white border-slate-200 h-[calc(100vh-280px)]">
                <CardHeader className="py-3 px-4 border-b border-slate-100">
                  <CardTitle className="text-slate-800 text-sm flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-blue-500" />
                    Mapa Operativo
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0 h-[calc(100%-56px)]">
                  <MapContainer
                    center={[14.6349, -90.5069]} // Guatemala City
                    zoom={12}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer
                      url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                      attribution='&copy; OpenStreetMap'
                    />
                    {/* Show emergency markers */}
                    {emergencies.map((em) => em.location && (
                      <Marker
                        key={em.id}
                        position={[em.location.lat, em.location.lng]}
                        icon={emergencyIcon}
                        eventHandlers={{
                          click: () => openEmergencyDetail(em)
                        }}
                      >
                        <Popup>
                          <div className="text-sm">
                            <p className="font-medium">{em.user?.full_name || 'Usuario'}</p>
                            <p className="text-slate-500">{em.severity} - {em.status}</p>
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                    {/* Show active service markers */}
                    {activeServices.map((service) => {
                      const loc = service.pickup_location || service.current_location;
                      if (!loc) return null;
                      return (
                        <Marker
                          key={service.id}
                          position={[loc.lat, loc.lng]}
                          icon={service.driver_id ? driverIcon : userIcon}
                        >
                          <Popup>
                            <div className="text-sm">
                              <p className="font-medium">{service.service_type}</p>
                              <p className="text-slate-500">{service.status}</p>
                            </div>
                          </Popup>
                        </Marker>
                      );
                    })}
                  </MapContainer>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Resolution Modal */}
      {showResolutionModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md bg-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-slate-800 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                Resolver Emergencia
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Notas de Resolución</label>
                <textarea
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  placeholder="Describe cómo se resolvió la emergencia..."
                  className="w-full mt-1 p-3 border border-slate-200 rounded-lg resize-none h-32 text-sm"
                  data-testid="resolution-notes-input"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowResolutionModal(false)}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleResolve}
                  disabled={!resolutionNotes.trim()}
                  className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                  data-testid="resolve-confirm-button"
                >
                  Confirmar Resolución
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
